import React, { useState, useEffect, useRef } from "react";
import {
  FaChevronRight,
  FaChevronLeft,
  FaTimes,
  FaChevronDown
} from "react-icons/fa";
import CloseIcon from "../images/white-close-icon.png";
import AppConstants from "../AppConstants";
import axios from "axios";

const MultiLevelSelect = ({ value = {}, onChange, reset }) => {
  const [data, setData] = useState([]);
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setPath([]);
    setSearchTerm("");
    setLoading(false);
  }, [reset]);

  useEffect(() => {
    async function loadProfessions() {
      try {
        const res = await fetch(
          `${AppConstants.Jobs_API}/professions/getAllProfessions`
        );
        const json = await res.json();
        const professions = json.data.map((item) => ({
          name: item.profession,
          id: item._id,
          children: []
        }));
        setData(professions);
      } catch (e) {
        console.error("Failed to fetch professions", e);
      }
    }

    loadProfessions();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(""); // Clear search when closing
      }
    };

    // Add event listener when dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleProfessionClick = async (profession) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${AppConstants.Jobs_API}/certification/getBullhornMatchedCertifications/${profession.id}`
      );
      const certsData = res.data.data;

      const certifications = certsData.map((cert) => ({
        name: cert.certification,
        id: cert._id,
        children: []
      }));

      setData((prevData) =>
        prevData.map((item) =>
          item.id === profession.id
            ? { ...item, children: certifications }
            : item
        )
      );

      setPath((prev) => [...prev, profession.name]);
      setSearchTerm(""); // Clear search when navigating
      onChange({
        profession: profession.name,
        certification: null,
        speciality: []
      });
    } catch (e) {
      console.error("Failed to fetch certifications", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCertificationClick = async (profession, certification) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${AppConstants.Jobs_API}/specialities/getBullhornMatchedSpecialities/${certification.id}`
      );
      const specsData = res.data.data;
      const specialities = specsData.map((s) => s.speciality);

      setData((prevData) =>
        prevData.map((p) =>
          p.id === profession.id
            ? {
                ...p,
                children: p.children.map((c) =>
                  c.id === certification.id
                    ? { ...c, children: specialities }
                    : c
                )
              }
            : p
        )
      );

      setPath((prev) => [...prev, certification.name]);
      setSearchTerm(""); // Clear search when navigating
      onChange({
        profession: path[0], // keep existing profession
        certification: [certification.name],
        speciality: []
      });
    } catch (e) {
      console.error("Failed to fetch specialities", e);
    } finally {
      setLoading(false);
    }
  };

  const currentData = path.reduce((acc, item) => {
    const found = acc.find((i) => i.name === item);
    return found?.children || [];
  }, data);

  const isFinalLevel =
    typeof currentData[0] === "string" || currentData.length === 0;

  const handleNext = async (name) => {
    const current = path.reduce((acc, item) => {
      const found = acc.find((i) => i.name === item);
      return found?.children || [];
    }, data);

    const selected = current.find((i) => i.name === name);

    if (!selected) return;

    if (path.length === 0) {
      await handleProfessionClick(selected);
    } else if (path.length === 1) {
      const selectedProfession = data.find((p) => p.name === path[0]);
      if (selectedProfession) {
        await handleCertificationClick(selectedProfession, selected);
      }
    } else {
      setPath((prev) => [...prev, name]);
    }
  };

  const handleBack = () => {
    setPath((prev) => prev.slice(0, -1));
    setSearchTerm(""); // Clear search when going back
  };

  const toggleSelect = (item) => {
    let updated;
    if (value.speciality?.includes(item)) {
      updated = value.speciality.filter((i) => i !== item);
    } else {
      updated = [...(value.speciality || []), item];
    }
    onChange({
      ...value,
      speciality: updated
    });
    // Removed setIsOpen(false) to keep dropdown open for multi-select
  };

  const handleSelectAll = () => {
    const all = currentData;
    const allSelected = all.every((i) => value.speciality?.includes(i));
    const updated = allSelected
      ? value.speciality.filter((i) => !all.includes(i))
      : [
          ...(value.speciality || []),
          ...all.filter((i) => !value.speciality.includes(i))
        ];

    onChange({ speciality: updated });
    // Removed setIsOpen(false) to keep dropdown open for multi-select
  };

  const removeTag = (item) => {
    const updatedSpeciality = value.speciality.filter((i) => i !== item);

    // if (updatedSpeciality.length === 0) {
    //   // Reset everything if all specialities are removed
    //   onChange({
    //     profession: "",
    //     certification: "",
    //     speciality: []
    //   });
    // } else {
    // Otherwise just update the specialities
    onChange({
      ...value,
      speciality: updatedSpeciality
    });
    // }
  };

  // Filter current data based on search term
  const filteredData = currentData.filter((item) => {
    const itemName = typeof item === "string" ? item : item.name;
    return itemName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      className="multi-level-select custom_multilevel_select"
      ref={dropdownRef}
    >
      {/* Select input box */}
      <div className="mls-input-box" onClick={() => setIsOpen(!isOpen)}>
        <span className="mls-placeholder">
          {value.speciality && value.speciality.length > 0
            ? `${value.speciality.length} selected`
            : "Select.."}
        </span>
        <FaChevronDown className={`mls-chevron ${isOpen ? "open" : ""}`} />
      </div>

      {/* Dropdown with scroll control */}
      {isOpen && (
        <div className="mls-dropdown">
          {path.length > 0 && (
            <div className="mls-back-btn" onClick={handleBack}>
              <FaChevronLeft /> Back to{" "}
              {path.length === 2 ? "Profession Type" : "Profession"}
            </div>
          )}

          {/* Search input */}
          <div className="mls-search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="mls-search-input"
              placeholder={`Search ${
                path.length === 0
                  ? "Professions"
                  : path.length === 1
                  ? "Profession Types"
                  : "Specialties"
              }...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="mls-menu-container">
            <ul className="mls-menu-list">
              {!isFinalLevel ? (
                filteredData.length > 0 ? (
                  filteredData.map((item, idx) => (
                    <li
                      key={idx}
                      className="mls-menu-item"
                      onClick={() => handleNext(item.name)}
                    >
                      {item.name} <FaChevronRight />
                    </li>
                  ))
                ) : (
                  <li className="mls-no-results">
                    No results found for "{searchTerm}"
                  </li>
                )
              ) : (
                <>
                  {filteredData.length > 0 && (
                    <li className="mls-menu-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={filteredData.every((i) =>
                            value.speciality?.includes(i)
                          )}
                          onChange={() => {
                            const allSelected = filteredData.every((i) =>
                              value.speciality?.includes(i)
                            );
                            const updated = allSelected
                              ? value.speciality.filter(
                                  (i) => !filteredData.includes(i)
                                )
                              : [
                                  ...(value.speciality || []),
                                  ...filteredData.filter(
                                    (i) => !value.speciality?.includes(i)
                                  )
                                ];
                            onChange({ ...value, speciality: updated });
                          }}
                        />
                        <span>Select All {searchTerm ? "Filtered" : ""}</span>
                      </label>
                    </li>
                  )}
                  {filteredData.length > 0 ? (
                    filteredData.map((item, idx) => (
                      <li key={idx} className="mls-menu-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={value.speciality?.includes(item)}
                            onChange={() => toggleSelect(item)}
                          />
                          <span>{item}</span>
                        </label>
                      </li>
                    ))
                  ) : (
                    <li className="mls-no-results">
                      No results found for "{searchTerm}"
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>

          {/* Loading indicator */}
          {loading && <div className="mls-loading">Loading...</div>}
        </div>
      )}

      {/* Grouped selected tags below the field */}
      {value.speciality && value.speciality.length > 0 && (
        <div
          className="mls-selected-tags"
          style={
            value.speciality.length > 5
              ? { height: "200px", overflowY: "scroll" }
              : {}
          }
        >
          <div className="mls-tag-list mt-2">
            {value.speciality.map((item) => (
              <span className="mls-tag selected-tags m-0" key={item}>
                <em>{item}</em>
                <img
                  className="mls-close-icon"
                  onClick={() => removeTag(item)}
                  src={CloseIcon}
                  alt="close"
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLevelSelect;
